import moment from 'moment'
import {
  AppointmentStatus,
  type AppointmentResponse,
} from '@dataTypes/appointment.type'
import { getAllAppointments } from '@services/api/appointment.api'
import { getFullName } from '@utils/fullname.util'

const isDevelopment = process.env.NODE_ENV === 'development'

const DEV_FALLBACK_SCHEDULE_LIST = [
  '[Development only — not live patient data]',
  'Example Patient, 9:30 AM',
  'Example Patient, 11:00 AM',
]

export type TodayScheduleResult = {
  scheduleList: string[]
  summary: string
}

const apptStart = (appt: AppointmentResponse): moment.Moment =>
  moment(appt.appointment_start_date || `${appt.appt_date} ${appt.appt_start}`)

const formatApptLine = (appt: AppointmentResponse): string => {
  const name = getFullName(appt.patient, false, false, 'Patient')
  const start = apptStart(appt)
  const time = start.isValid() ? start.format('h:mm A') : appt.appt_start
  return `${name}, ${time}`
}

const buildSummary = (appointments: AppointmentResponse[]): string => {
  if (appointments.length === 0) {
    return 'There are no appointments scheduled today.'
  }

  const confirmed = appointments.filter(
    (a) => a.appt_status === AppointmentStatus.Confirmed,
  ).length
  const pending = appointments.filter(
    (a) =>
      a.appt_status === AppointmentStatus.Pending ||
      a.appt_status === AppointmentStatus.Unconfirmed,
  ).length
  const canceled = appointments.filter(
    (a) => a.appt_status === AppointmentStatus.Cancelled,
  ).length

  const upcoming = [...appointments]
    .filter((a) => a.appt_status !== AppointmentStatus.Cancelled)
    .sort((a, b) => apptStart(a).diff(apptStart(b)))[0]

  let nextLine = ''
  if (upcoming) {
    const name = getFullName(upcoming.patient, false, false, 'Patient')
    const start = apptStart(upcoming)
    const time = start.isValid() ? start.format('h:mm A') : upcoming.appt_start
    const type = upcoming.appointmenttype?.name
    nextLine = `\n\nNext appointment: ${time} – ${name}${type ? ` (${type})` : ''}`
  }

  const countLabel = appointments.length === 1 ? 'appointment' : 'appointments'
  return `You have ${appointments.length} ${countLabel} scheduled today:\n\n${confirmed} confirmed\n${pending} pending confirmation\n${canceled} canceled${nextLine}`
}

export const loadTodaySchedule = async (): Promise<TodayScheduleResult> => {
  const today = moment().format('YYYY-MM-DD')
  const response = await getAllAppointments(today, today)
  const appointments = response.data?.appointments ?? []

  if (response.statusCode === 200) {
    const sorted = [...appointments].sort((a, b) =>
      apptStart(a).diff(apptStart(b)),
    )
    return {
      scheduleList:
        sorted.length > 0
          ? sorted.map(formatApptLine)
          : ['No appointments scheduled today.'],
      summary: buildSummary(sorted),
    }
  }

  if (isDevelopment) {
    return {
      scheduleList: DEV_FALLBACK_SCHEDULE_LIST,
      summary:
        '[Development only — not live data]\nUnable to load today’s schedule. Showing an example fallback.',
    }
  }

  return {
    scheduleList: ['Unable to load today’s schedule.'],
    summary:
      'Unable to load today’s schedule. Please try again or open the calendar.',
  }
}
